import { OLButton } from "OLButton";
import { hasAnyAccessToResource, hasModifyAccessToResource } from "api/general";
import { Subheader } from "components/Header/Subheader/Subheader";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { OLTabs } from "components/Tabs/OLTabs";
import { useMessage } from "hooks/intl";
import { useActiveTab } from "hooks/useActiveTab";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { links } from "utilities/links";
import { AdminList } from "../../Admin/AdminList/AdminList";
import { TranslationList } from "../../Language/TranslationList/TranslationList";
import { RoleList } from "../../Role/RoleList/RoleList";
import { StoreList } from "../../Store/StoreList/StoreList";

export const tabLabels = {
  roles: "roles",
  admins: "admins",
  tenants: "tenants",
  translations: "translations",
} as const;

type TabLabel = (typeof tabLabels)[keyof typeof tabLabels];

type Tab = {
  label: TabLabel;
  isShown: boolean;
};

interface ContextButtonProps {
  link?: string;
  label: string;
  disabled?: boolean;
  "data-test-id": string;
}

const ContextButton = (props: ContextButtonProps) => {
  const { message } = useMessage();

  return (
    <Link to={props.link || ""}>
      <OLButton
        disabled={props.disabled}
        data-test-id={props["data-test-id"]}
        variant="contained"
      >
        {message({ id: props.label })}
      </OLButton>
    </Link>
  );
};

export const Settings = () => {
  const { message } = useMessage();
  const { activeTab, changeActiveTab } = useActiveTab({
    id: "settings",
    tab: tabLabels.roles,
  });

  const hasAnyRolePermission = hasAnyAccessToResource("ACL");
  const hasModifyRolePermission = hasModifyAccessToResource("ACL");
  const hasAnyAdminPermission = hasAnyAccessToResource("ADMIN");
  const hasModifyAdminPermission = hasModifyAccessToResource("ADMIN");
  const hasModifyLanguagePermission = hasModifyAccessToResource("LANGUAGE");
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      changeActiveTab(links.settings, "settings", hash.replace("#", ""));
    }
  }, [hash, changeActiveTab]);

  const tabsList: Tab[] = [
    {
      label: tabLabels.roles,
      isShown: hasAnyRolePermission,
    },
    {
      label: tabLabels.admins,
      isShown: hasAnyAdminPermission,
    },
    { label: tabLabels.tenants, isShown: true },
    { label: tabLabels.translations, isShown: true },
  ];

  const isRolesTabActive = activeTab === tabLabels.roles;
  const isAdminsTabActive = activeTab === tabLabels.admins;
  const isTenantsTabActive = activeTab === tabLabels.tenants;
  const isTranslationsTabActive = activeTab === tabLabels.translations;

  const tabsFilteredByPermission = tabsList
    .filter((tab) => tab.isShown)
    .map((tab) => tab.label);

  const getButtonProps = () => {
    switch (activeTab) {
      case tabLabels.roles:
        return {
          label: "addRole",
          ...(hasModifyRolePermission
            ? { link: links.addRole }
            : { disabled: true }),
          "data-test-id": "role-list",
        };
      case tabLabels.admins:
        return {
          label: "addAdmin",
          ...(hasModifyAdminPermission
            ? { link: links.addAdmin }
            : { disabled: true }),
          "data-test-id": "admin-list",
        };
      case tabLabels.tenants:
        return {
          label: "addTenant",
          link: links.addStore,
          "data-test-id": "tenant-list",
        };
      case tabLabels.translations:
        return {
          label: "addLanguage",
          ...(hasModifyLanguagePermission
            ? { link: links.addLanguage }
            : { disabled: true }),
          "data-test-id": "add-language",
        };
      default:
        return {
          label: "",
          link: "",
          disabled: false,
          "data-test-id": "",
        };
    }
  };

  const buttonProps = getButtonProps();

  return (
    <>
      <TopHeader title={message({ id: "globalManagement" })} />
      <Subheader>
        <OLTabs
          items={tabsFilteredByPermission}
          value={activeTab}
          data-test-id="settings"
          onChange={(_: React.SyntheticEvent, newValue: TabLabel) =>
            changeActiveTab(links.settings, "settings", newValue)
          }
        />
        {!!buttonProps.label && <ContextButton {...buttonProps} />}
      </Subheader>
      {isRolesTabActive && <RoleList />}
      {isAdminsTabActive && <AdminList />}
      {isTenantsTabActive && <StoreList />}
      {isTranslationsTabActive && <TranslationList />}
    </>
  );
};
